import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import "./contactSection.css";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import AnimatedBlock from "./animatedBlock";
import { useEffect } from "react";
import emailjs from '@emailjs/browser';

// ─── Meeting Time Picker ──────────────────────────────────────────────────────

const TIME_SLOTS = (() => {
    const slots = [];
    for (let h = 9; h <= 16; h++) {
        for (const m of [0, 30]) {
            const period  = h < 12 ? 'AM' : 'PM';
            const display = h > 12 ? h - 12 : h;
            slots.push({ hour: h, minute: m, label: `${display}:${m === 0 ? '00' : '30'} ${period}` });
        }
    }
    return slots;
})();

const slotKey = (date, slot) =>
    `${date.toDateString()}__${slot.hour}_${slot.minute}`;

const slotDisplay = (date, slot) => {
    const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${DAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()} at ${slot.label}`;
};

const isSlotPast = (date, slot) => {
    const now = new Date();
    if (date.toDateString() !== now.toDateString()) return false;
    const t = new Date(date);
    t.setHours(slot.hour, slot.minute, 0, 0);
    return t < now;
};

const dateHeader = (date) => {
    const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${DAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}`;
};

const nextWorkday = (date, direction) => {
    const d = new Date(date);
    do { d.setDate(d.getDate() + direction); }
    while (d.getDay() === 0 || d.getDay() === 6);
    return d;
};

const MeetingTimePicker = ({ selectedTimes, onToggle, calendarDate, onDateChange }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const prevDisabled = nextWorkday(calendarDate, -1) < today;

    return (
        <div className="mtp-picker">
            <div className="mtp-header">
                <button type="button" className="mtp-nav" onClick={() => onDateChange(nextWorkday(calendarDate, -1))} disabled={prevDisabled}>&#8249;</button>
                <span className="mtp-date-label">{dateHeader(calendarDate)}</span>
                <button type="button" className="mtp-nav" onClick={() => onDateChange(nextWorkday(calendarDate, 1))}>&#8250;</button>
            </div>
            <div className="mtp-grid">
                {TIME_SLOTS.map(slot => {
                    const past     = isSlotPast(calendarDate, slot);
                    const key      = slotKey(calendarDate, slot);
                    const selected = selectedTimes.some(t => t.key === key);
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`mtp-slot${selected ? ' mtp-slot--selected' : ''}${past ? ' mtp-slot--past' : ''}`}
                            onClick={() => onToggle(calendarDate, slot)}
                            disabled={past}
                        >
                            {slot.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const ContactSection = () => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        requestType: "",
        message: "",
    });

    // Pre-fill form based on URL parameter (example: ?requestType=Demo)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const presetType = params.get("requestType");

        if (presetType) {
            setFormData((prev) => ({
                ...prev,
                requestType: presetType,
            }));
        }
    }, []);

    const [status, setStatus] = useState(null); // success | error | null
    const [loading, setLoading] = useState(false);

    const [suggestMeetings, setSuggestMeetings] = useState(false);
    const [selectedTimes, setSelectedTimes]     = useState([]);
    const [calendarDate, setCalendarDate]       = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
        return d;
    });

    const handleToggleSlot = (date, slot) => {
        const key     = slotKey(date, slot);
        const display = slotDisplay(date, slot);
        const ts      = new Date(date);
        ts.setHours(slot.hour, slot.minute, 0, 0);
        setSelectedTimes(prev =>
            prev.some(t => t.key === key)
                ? prev.filter(t => t.key !== key)
                : [...prev, { key, display, ts: ts.getTime() }].sort((a, b) => a.ts - b.ts)
        );
    };

    const handleSuggestToggle = (val) => {
        setSuggestMeetings(val);
        if (!val) {
            setSelectedTimes([]);
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
            setCalendarDate(d);
        }
    };

    const isFormFilled =
        formData.firstName.trim() !== "" &&
        formData.lastName.trim() !== "" &&
        formData.email.trim() !== "" &&
        formData.phone.trim() !== "" &&
        formData.company.trim() !== "" &&
        formData.requestType !== "";

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const meetingTimesStr = suggestMeetings && selectedTimes.length > 0
                ? `${selectedTimes.map(t => t.display).join('\n')}\n\n(Times shown in the requester's local time zone: ${timeZone})`
                : '';

            await emailjs.send(
                'service_3fxzp8s',
                'template_vhlehg7',
                {
                    firstName:     formData.firstName,
                    lastName:      formData.lastName,
                    email:         formData.email,
                    phone:         formData.phone,
                    company:       formData.company,
                    requestType:   formData.requestType,
                    message:       formData.message,
                    meeting_times: meetingTimesStr,
                },
                { publicKey: 'Nx44CgjpAjr6nxQmc' }
            );

            setStatus("success");
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                company: "",
                requestType: "",
                message: "",
            });
            setSuggestMeetings(false);
            setSelectedTimes([]);
            const resetDate = new Date();
            resetDate.setHours(0, 0, 0, 0);
            while (resetDate.getDay() === 0 || resetDate.getDay() === 6) resetDate.setDate(resetDate.getDate() + 1);
            setCalendarDate(resetDate);
        } catch (err) {
            console.error("EmailJS error:", err);
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="page-section section-style section-style-contact">
            <div className="cts-content cts-two-column">

                {/* LEFT COLUMN */}
                <AnimatedBlock>
                    <div className="cts-column cts-left-column">
                        <h1>Contact Khor Developer</h1>
                        <p>
                            Reach out using the form or email us directly.
                            <br /><br />
                            <strong>Email</strong><br />
                            <a href="mailto:support@khordeveloper.com">
                                support@khordeveloper.com
                            </a>
                        </p>
                    </div>
                </AnimatedBlock>

                {/* RIGHT COLUMN */}
                <div className="cts-column cts-right-column">
                    {status ? (
                        <div className="cts-message-container">
                            {status === "success" ? (
                                <div className="cts-status-card cts-status-card--success">
                                    <div className="cts-status-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="9 12 11 14 15 10" />
                                        </svg>
                                    </div>
                                    <h2 className="cts-status-heading">Message Sent</h2>
                                    <p className="cts-status-body">A member of our team will contact you shortly.</p>
                                    <button className="cts-status-btn" onClick={() => setStatus(null)}>Send Another Message</button>
                                </div>
                            ) : (
                                <div className="cts-status-card cts-status-card--error">
                                    <div className="cts-status-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                    </div>
                                    <h2 className="cts-status-heading">Submission Failed</h2>
                                    <p className="cts-status-body">We were unable to send your message. Please email us directly at <a href="mailto:support@khordeveloper.com">support@khordeveloper.com</a>.</p>
                                    <button className="cts-status-btn" onClick={() => setStatus(null)}>Try Again</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <form className="cts-form" onSubmit={handleSubmit}>

                            {/* Name */}
                            <div className="cts-form-group">
                                <label>
                                    Name <span className="cts-required">*</span>
                                </label>
                                <div className="cts-form-row">
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder="First Name"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                    />
                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder="Last Name"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="cts-form-group">
                                <label>
                                    Email <span className="cts-required">*</span>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {/* Phone */}
                            <div className="cts-form-group">
                                <label>
                                    Phone <span className="cts-required">*</span>
                                    <input
                                        type="text"
                                        name="phone"
                                        placeholder="(###) ###-####"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {/* Company */}
                            <div className="cts-form-group">
                                <label>
                                    Company <span className="cts-required">*</span>
                                    <input
                                        type="text"
                                        name="company"
                                        required
                                        value={formData.company}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {/* Request Type */}
                            <div className="cts-form-group">
                                <label>
                                    Request Type <span className="cts-required">*</span>
                                </label>

                                <div className="cts-radio-group">
                                    {["Support", "Onboarding", "Demo"].map((type) => (
                                        <label key={type} className="cts-radio">
                                            <input
                                                type="radio"
                                                name="requestType"
                                                value={type}
                                                required
                                                checked={formData.requestType === type}
                                                onChange={handleChange}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Share Availability */}
                            <div className="cts-form-group">
                                <div className="cts-switch-row">
                                    <label>Share Availability</label>
                                    <label className="mtp-switch">
                                        <input
                                            type="checkbox"
                                            checked={suggestMeetings}
                                            onChange={e => handleSuggestToggle(e.target.checked)}
                                        />
                                        <span className="mtp-slider" />
                                    </label>
                                </div>

                                {suggestMeetings && (
                                    <>
                                        <MeetingTimePicker
                                            selectedTimes={selectedTimes}
                                            onToggle={handleToggleSlot}
                                            calendarDate={calendarDate}
                                            onDateChange={setCalendarDate}
                                        />
                                        <p className="mtp-summary">
                                            {selectedTimes.length > 0
                                                ? <><span className="mtp-summary-count">{selectedTimes.length} time{selectedTimes.length !== 1 ? 's' : ''} selected</span> — click a selected slot to deselect</>
                                                : 'Select one or more times that work for you.'}
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* Message */}
                            <div className="cts-form-group">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={3}
                                    value={formData.message}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Submit */}
                            <div className="cts-form-group">
                                <button className="btn" type="submit" disabled={!isFormFilled || loading}>
                                    {loading ? <div className="spinner" /> : "Submit"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

const Contact = () => (
    <div className="App">
        <Helmet>
            <title>Contact Khor Developer | Book a CRE Software Demo</title>
            <meta name="description" content="Schedule a demo or get in touch with the Khor Developer team. See how our Excel-native CRE underwriting platform fits your investment workflow." />
            <meta property="og:title" content="Contact Khor Developer | Book a CRE Software Demo" />
            <meta property="og:url" content="https://www.khordeveloper.com/contact" />
            <link rel="canonical" href="https://www.khordeveloper.com/contact" />
        </Helmet>
        <ReactHeader />
        <ContactSection />
        <FooterSection />
    </div>
);

export default Contact;

